import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { EdgeResizer } from '~src/utils';
import { colSizes } from '~loader/loader-utils';
import { duplicateTemplate } from '~loader/loader-api/duplicateTemplate';
import { deleteTemplate } from '~loader/loader-api/deleteTemplate';
import { renameTemplate } from '~loader/loader-api/renameTemplate';
import { onTemplateImportSettingsClick } from '~core/templateImportSettings';
import { router } from '~src/router';
import styles from './RecentTemplateItem.module.scss';

const getTemplatePages = (template) => {
  const edges = template.editor_templates_pages?.edges || [];

  return edges
    .map((edge) => edge.node)
    .sort((a, b) => (a.order || 0) - (b.order || 0));
};

const getTemplateCategoryName = (template) =>
  template.editor_templates_categories_pointer?.name ||
  template.editor_templates_categories_default_pointer?.name ||
  template.metadata?.templatesCategoryName ||
  '';

const getTemplateProjectTypeName = (template) =>
  template.editor_project_types_pointer?.name ||
  template.editor_project_types_default_pointer?.name ||
  template.metadata?.projectTypeName ||
  '';

const getTemplateFormatName = (template) =>
  template.editor_project_formats_pointer?.name ||
  template.editor_project_formats_default_pointer?.name ||
  template.metadata?.formatName ||
  '';

const stopMenuEvent = (event) => {
  event.preventDefault();
  event.stopPropagation();
};

function RecentTemplateItem({ template, allowDelete, onTemplatesChanged }) {
  const [isActionsVisible, setIsActionsVisible] = useState(false);
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const pages = getTemplatePages(template);
  const gifPreviewId = pages[0]?.gifPreviewId;
  const pagePreviews = pages
    .map((page) => page.preview?.url)
    .filter(Boolean)
    .slice(0, 2);
  const thumbnails = gifPreviewId
    ? [`${process.env.GIOTTO_VIDEO_CDN}/${gifPreviewId}`]
    : pagePreviews.map((preview) => new EdgeResizer(preview).scaleToWidth(210));
  const isFallback = thumbnails.length === 0;
  const previewImages = isFallback
    ? [`${process.env.OFC_CDN_URL}/logos/giotto_white.png`]
    : thumbnails;
  const templateUrl = `/template-manager/${template.objectId}`;
  const details = [
    getTemplateCategoryName(template),
    getTemplateProjectTypeName(template),
    getTemplateFormatName(template),
  ].filter(Boolean);

  useEffect(() => {
    if (!isActionsVisible) {
      setIsActionsOpen(false);
    }
  }, [isActionsVisible]);

  const closeActionsMenu = (event) => {
    stopMenuEvent(event);
    setIsActionsOpen(false);
  };

  const handleDuplicate = async (event) => {
    closeActionsMenu(event);
    const duplicated = await duplicateTemplate(template.objectId);
    if (duplicated && onTemplatesChanged) {
      await onTemplatesChanged();
    }
  };

  const handleRename = async (event) => {
    closeActionsMenu(event);
    const renamed = await renameTemplate(template.objectId, false, {
      refreshSidebar: false,
    });
    if (renamed && onTemplatesChanged) {
      await onTemplatesChanged();
    }
  };

  const handleDelete = async (event) => {
    closeActionsMenu(event);
    const deleted = await deleteTemplate(template, false);
    if (deleted && onTemplatesChanged) {
      await onTemplatesChanged();
    }
  };

  const handleImportSettings = async (event) => {
    closeActionsMenu(event);
    const updated = await onTemplateImportSettingsClick(template.objectId);
    if (updated && onTemplatesChanged) {
      await onTemplatesChanged();
    }
  };

  const handleEdit = (event) => {
    closeActionsMenu(event);
    router.navigate(templateUrl);
  };

  return (
    <div
      data-editor-template-id={template.objectId}
      className={`${colSizes['regular']} mt-5 block-default ${styles.templateCard}`}
      onMouseEnter={() => setIsActionsVisible(true)}
      onMouseLeave={() => setIsActionsVisible(false)}
    >
      <div
        className={styles.templateActionsButton}
        style={{ display: isActionsVisible ? 'block' : 'none' }}
        onClick={(event) => {
          stopMenuEvent(event);
          setIsActionsOpen((isOpen) => !isOpen);
        }}
      >
        <div className={styles.templateActionsRelative}>
          <div
            className={styles.templateActionsMenu}
            style={{ display: isActionsOpen ? 'block' : 'none' }}
          >
            <button type="button" onClick={handleDuplicate}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M8 16H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v2m-6 12h8a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-8a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2z"
                />
              </svg>
              Duplica
            </button>
            <a href={templateUrl} onClick={handleEdit}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M11 5H6a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2v-5m-1.414-9.414a2 2 0 1 1 2.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
              Modifica
            </a>
            <button type="button" onClick={handleRename}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M18 13V5a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h3l3 3 3-3h3a2 2 0 0 0 2-2zM5 7a1 1 0 0 1 1-1h8a1 1 0 1 1 0 2H6a1 1 0 0 1-1-1zm1 3a1 1 0 1 0 0 2h3a1 1 0 1 0 0-2H6z"
                  clipRule="evenodd"
                />
              </svg>
              Rinomina
            </button>
            {allowDelete && (
              <button
                type="button"
                className={styles.templateDeleteAction}
                onClick={handleDelete}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 7l-.867 12.142A2 2 0 0 1 16.138 21H7.862a2 2 0 0 1-1.995-1.858L5 7m5 4v6m4-6v6M9 7V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3m-8 0h10"
                  />
                </svg>
                Elimina
              </button>
            )}
            <button type="button" onClick={handleImportSettings}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 0 0-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 0 0-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 0 0-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 0 0-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 0 0 1.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0z"
                />
              </svg>
              DPI / Multiplier
            </button>
          </div>
        </div>
      </div>
      <a href={templateUrl} data-navigo>
        <div className={styles.templatePreviewContainer}>
          {previewImages.map((thumbnail) => (
            <img
              key={thumbnail}
              className={`${styles.templatePreviewImage} ${
                isFallback ? styles.fallbackPreviewImage : ''
              } ${previewImages.length > 1 ? styles.widthTwoPages : ''}`}
              src={thumbnail}
              loading="lazy"
              alt=""
            />
          ))}
        </div>
      </a>
      <div className={styles.templateMeta}>
        <div className={styles.templateName}>
          {template.name || 'Modello Senza Titolo'}
        </div>
        <div className={styles.templateSubTitle}>
          {details.length > 0 ? details.join(' | ') : 'Modello'}
        </div>
      </div>
    </div>
  );
}

RecentTemplateItem.propTypes = {
  template: PropTypes.object.isRequired,
  allowDelete: PropTypes.bool,
  onTemplatesChanged: PropTypes.func,
};

RecentTemplateItem.defaultProps = {
  allowDelete: false,
};

export default RecentTemplateItem;
